import { api } from '../../app/api';

import type {
    BulkDeleteResult,
    Category,
    CategoryDetail,
    ImportProductsCsvArgs,
    ImportProductsCsvResult,
    ListProductsArgs,
    OutstandingTransfer,
    Product,
    ProductForm,
    ProductList,
    ProductStatus,
    StockAccount,
    Supplier,
    Unit,
} from './products.schema';
import {
    CategoryDetailSchema,
    CategoryListSchema,
    CsvImportApplyResultSchema,
    CsvImportPreviewSchema,
    isCsvImportPreview,
    OutstandingTransferSchema,
    BulkDeleteResultSchema,
    ProductSchema,
    StockAccountListSchema,
    SupplierListSchema,
    UnitListSchema,
} from './products.schema';

// The outstanding-transfer query has no entity of its own — piggyback on the
// Product tag type with a reserved id so transfers can invalidate it.
const OUTSTANDING_TAG = { type: 'Product' as const, id: 'OUTSTANDING-TRANSFER' };

const perIdAndList = (ids: number[]) => [
    ...ids.map((id) => ({ type: 'Product' as const, id })),
    { type: 'Product' as const, id: 'LIST' },
];

const productsApi = api.injectEndpoints({
    endpoints: (builder) => ({
        listProducts: builder.query<ProductList, ListProductsArgs>({
            query: ({
                start = 0,
                limit = 30,
                q,
                search,
                sort = 'id',
                direction = 'desc',
                categoryId,
                status,
                availability,
                tagIds,
                supplierId,
                priceMin,
                priceMax,
                hasImage,
                stock,
                activeOnline,
            } = {}) => ({
                url: '/v2/products',
                method: 'GET',
                params: {
                    _start: start,
                    _end: start + limit,
                    _sort: sort,
                    _order: direction,
                    q: q || search || undefined,
                    categoryId: categoryId || undefined,
                    status: status || undefined,
                    availability: availability || undefined,
                    tagIds: tagIds && tagIds.length ? tagIds.join(',') : undefined,
                    supplierId: supplierId || undefined,
                    priceMin: priceMin === '' || priceMin == null ? undefined : priceMin,
                    priceMax: priceMax === '' || priceMax == null ? undefined : priceMax,
                    hasImage: hasImage ?? undefined,
                    stock: stock || undefined,
                    activeOnline: activeOnline ?? undefined,
                },
            }),
            // Per-row tolerance: one malformed row must not blank the page.
            transformResponse: (raw: unknown) => {
                const env = raw as { data?: unknown[]; count?: number };
                const list = Array.isArray(env?.data) ? env.data : [];
                const data: Product[] = [];
                for (const row of list) {
                    const r = ProductSchema.safeParse(row);
                    if (r.success) data.push(r.data);
                    else if (process.env.NODE_ENV !== 'production')
                        // eslint-disable-next-line no-console
                        console.warn('Product row failed validation:', r.error.issues[0]);
                }
                return { data, count: Number(env?.count ?? data.length) };
            },
            providesTags: (result) =>
                result
                    ? [
                          ...result.data.map(({ id }) => ({ type: 'Product' as const, id })),
                          { type: 'Product' as const, id: 'LIST' },
                      ]
                    : [{ type: 'Product' as const, id: 'LIST' }],
        }),

        getProduct: builder.query<Product, number>({
            query: (id) => ({
                url: '/products/[id]',
                method: 'GET',
                query: { id },
            }),
            transformResponse: (raw: { data: unknown }) => ProductSchema.parse(raw.data),
            providesTags: (_r, _e, id) => [{ type: 'Product', id }],
        }),

        createProduct: builder.mutation<Product, ProductForm>({
            query: (body) => ({
                url: '/products',
                method: 'POST',
                body: serializeProductForm(body),
            }),
            transformResponse: (raw: { data: unknown }) => ProductSchema.parse(raw.data),
            invalidatesTags: [{ type: 'Product', id: 'LIST' }],
        }),

        updateProduct: builder.mutation<Product, { id: number; values: ProductForm }>({
            query: ({ id, values }) => ({
                url: '/products/[id]',
                method: 'PUT',
                query: { id },
                body: serializeProductForm(values, id),
            }),
            transformResponse: (raw: { data: unknown }) => ProductSchema.parse(raw.data),
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'Product', id },
                { type: 'Product', id: 'LIST' },
            ],
        }),

        deleteProduct: builder.mutation<void, number>({
            query: (id) => ({
                url: '/products/[id]',
                method: 'DELETE',
                query: { id },
            }),
            invalidatesTags: (_r, _e, id) => [
                { type: 'Product', id },
                { type: 'Product', id: 'LIST' },
            ],
        }),

        activateProduct: builder.mutation<void, number>({
            query: (id) => ({
                url: '/products/[id]/activate',
                method: 'PUT',
                query: { id },
                body: {},
            }),
            invalidatesTags: (_r, _e, id) => [
                { type: 'Product', id },
                { type: 'Product', id: 'LIST' },
            ],
        }),

        deactivateProduct: builder.mutation<void, number>({
            query: (id) => ({
                url: '/products/[id]/deactivate',
                method: 'PUT',
                query: { id },
                body: {},
            }),
            invalidatesTags: (_r, _e, id) => [
                { type: 'Product', id },
                { type: 'Product', id: 'LIST' },
            ],
        }),

        setProductStatus: builder.mutation<void, { id: number; status: ProductStatus }>({
            query: ({ id, status }) => ({
                url: '/products/[id]/status',
                method: 'PUT',
                query: { id },
                body: { status },
            }),
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'Product', id },
                { type: 'Product', id: 'LIST' },
            ],
        }),

        duplicateProduct: builder.mutation<Product, number>({
            query: (id) => ({
                url: '/products/[id]/duplicate',
                method: 'PUT',
                query: { id },
                body: {},
            }),
            transformResponse: (raw: { data: unknown }) => ProductSchema.parse(raw.data),
            invalidatesTags: [{ type: 'Product', id: 'LIST' }],
        }),

        listCategories: builder.query<Category[], void>({
            query: () => ({
                url: '/categories',
                method: 'GET',
                params: { _start: 0, _end: 1000, _sort: 'title', _order: 'ASC' },
            }),
            transformResponse: (raw: unknown) => CategoryListSchema.parse(raw).data,
            providesTags: [{ type: 'Category', id: 'LIST' }],
        }),

        listSuppliers: builder.query<Supplier[], void>({
            query: () => ({
                url: '/suppliers',
                method: 'GET',
                params: { _start: 0, _end: 1000, _sort: 'name', _order: 'ASC' },
            }),
            transformResponse: (raw: unknown) => SupplierListSchema.parse(raw).data,
            providesTags: [{ type: 'Supplier', id: 'LIST' }],
        }),

        listUnits: builder.query<Unit[], void>({
            query: () => ({
                url: '/units',
                method: 'GET',
                params: { _start: 0, _end: 1000, _sort: 'name', _order: 'ASC' },
            }),
            transformResponse: (raw: unknown) => UnitListSchema.parse(raw).data,
            providesTags: [{ type: 'Unit', id: 'LIST' }],
        }),

        // ---- Bulk operations (POST /v2/products/bulk/*, max 500 ids) ----
        bulkSetActive: builder.mutation<void, { ids: number[]; active: boolean }>({
            query: (body) => ({ url: '/v2/products/bulk/set-active', method: 'POST', body }),
            invalidatesTags: (_r, _e, { ids }) => perIdAndList(ids),
        }),

        bulkSetStatus: builder.mutation<void, { ids: number[]; status: ProductStatus }>({
            query: (body) => ({ url: '/v2/products/bulk/set-status', method: 'POST', body }),
            invalidatesTags: (_r, _e, { ids }) => perIdAndList(ids),
        }),

        bulkAddTags: builder.mutation<void, { ids: number[]; tagIds: number[] }>({
            query: (body) => ({ url: '/v2/products/bulk/add-tags', method: 'POST', body }),
            invalidatesTags: (_r, _e, { ids }) => perIdAndList(ids),
        }),

        bulkSetCategory: builder.mutation<void, { ids: number[]; categoryId: number }>({
            query: (body) => ({ url: '/v2/products/bulk/set-category', method: 'POST', body }),
            invalidatesTags: (_r, _e, { ids }) => perIdAndList(ids),
        }),

        bulkDelete: builder.mutation<BulkDeleteResult, { ids: number[] }>({
            query: (body) => ({ url: '/v2/products/bulk/delete', method: 'POST', body }),
            transformResponse: (raw: unknown) =>
                BulkDeleteResultSchema.parse((raw as { data: unknown }).data),
            invalidatesTags: (_r, _e, { ids }) => perIdAndList(ids),
        }),

        // ---- CSV import (POST /v2/products/import). dryRun answers the
        // preview shape, apply answers the result shape — discriminate with
        // isCsvImportPreview() from products.schema. ----
        importProductsCsv: builder.mutation<ImportProductsCsvResult, ImportProductsCsvArgs>({
            query: ({ csv, dryRun = true, createLines, updateLines, options }) => ({
                url: '/v2/products/import',
                method: 'POST',
                body: { csv, dryRun, createLines, updateLines, options },
            }),
            transformResponse: (raw: unknown) => {
                const data = (raw as { data: unknown }).data;
                if (data && typeof data === 'object' && 'summary' in data) {
                    return CsvImportPreviewSchema.parse(data);
                }
                return CsvImportApplyResultSchema.parse(data);
            },
            // Apply must also drop the per-id caches of every touched product,
            // or a previously-visited view/edit page serves pre-import data
            // (and a save from that stale form reverts the import).
            invalidatesTags: (result, _e, { dryRun = true }) => {
                if (dryRun) return [];
                if (!result || isCsvImportPreview(result)) {
                    return [{ type: 'Product' as const, id: 'LIST' }];
                }
                return perIdAndList([...result.updated, ...result.created]);
            },
        }),

        // ---- Inventory transfer between stock accounts (1 = PL, 4 = BV) ----
        transferStock: builder.mutation<
            unknown,
            { source: number; target: number; quantity: number; productId: number }
        >({
            query: (body) => ({ url: '/inventory/transfer', method: 'POST', body }),
            invalidatesTags: (_r, _e, { productId }) => [
                ...perIdAndList([productId]),
                OUTSTANDING_TAG,
            ],
        }),

        outstandingTransfer: builder.query<OutstandingTransfer | null, void>({
            query: () => ({ url: '/inventory/outstanding-transfer', method: 'GET' }),
            // Non-standard envelope: { existing: StockOperation | null }.
            transformResponse: (raw: unknown) => {
                const existing = (raw as { existing?: unknown })?.existing;
                if (!existing) return null;
                const parsed = OutstandingTransferSchema.safeParse(existing);
                return parsed.success ? parsed.data : null;
            },
            providesTags: [OUTSTANDING_TAG],
        }),

        // ---- Category detail — attribute definitions (with enum values) for
        // the dynamic attributes section. ----
        getCategoryDetail: builder.query<CategoryDetail, number>({
            query: (id) => ({ url: '/categories/[id]', method: 'GET', query: { id } }),
            transformResponse: (raw: unknown) =>
                CategoryDetailSchema.parse((raw as { data: unknown }).data),
            providesTags: (_r, _e, id) => [{ type: 'Category', id }],
        }),

        listStockAccounts: builder.query<StockAccount[], void>({
            query: () => ({
                url: '/stock-accounts',
                method: 'GET',
                params: { _start: 0, _end: 100, _sort: 'id', _order: 'ASC' },
            }),
            transformResponse: (raw: unknown) => StockAccountListSchema.parse(raw).data,
        }),

        // ---- DesignEditor round-trip (image gallery "Editeaza") ----
        // Route an already-uploaded image through the API proxy to get a clean,
        // same-origin base64 dataURL the fabric canvas can read pixels from (the
        // CDN taints the canvas — no CORS). Mirrors KFileDrop.toEditableImage.
        // keepUnusedDataFor: 0 — these dataURLs are multi-MB; don't retain them
        // in the RTK cache once the editor closes.
        proxyImage: builder.query<string, string>({
            query: (url) => ({ url: '/image-proxy', method: 'GET', params: { url } }),
            transformResponse: (raw: unknown) => (raw as { data?: string })?.data ?? '',
            keepUnusedDataFor: 0,
        }),

        // Saved fabric design JSON for an already-uploaded image filename. The
        // route answers the raw ImageDesign row ({ designJson }); tolerate a
        // { data: { designJson } } envelope too. A 404 (no saved design) is
        // expected — the caller wraps this in try/catch.
        getImageDesign: builder.query<string | null, string>({
            query: (filename) => ({
                url: '/image-designs/[filename]',
                method: 'GET',
                query: { filename },
            }),
            transformResponse: (raw: unknown) => {
                const body = raw as {
                    designJson?: string;
                    data?: { designJson?: string } | null;
                };
                return body?.designJson ?? body?.data?.designJson ?? null;
            },
            keepUnusedDataFor: 0,
        }),
    }),
});

// The legacy API expects boolean flags as 0/1 and a few defaults. Mirrors
// Structures.Product.export() in src/structures/Product.js. `selfId` is the
// product's own id on updates — see the variantId note below.
function serializeProductForm(values: ProductForm, selfId?: number) {
    const bool = (v: boolean | undefined | null) => (v ? 1 : 0);
    const payload: Record<string, unknown> = {
        title: values.title,
        sku: values.sku,
        barcode: values.barcode,
        slug: values.slug,
        description: values.description,
        metaTitle: values.metaTitle,
        metaDescription: values.metaDescription,
        categoryId: values.categoryId,
        unitId: values.unitId || 1,
        // 'No variant' is variantId = self id on the wire, never NULL — the
        // storefront variants query matches on equality, and NULL would sweep
        // in every variant-less product. Creates may send null (the API
        // self-assigns it); updates send selfId.
        variantId: values.variantId ?? selfId ?? null,
        availabilitySupplierId: values.availabilitySupplierId || null,
        sourcePrice: values.sourcePrice,
        vatBracket: values.vatBracket,
        // Tri-state — null (nelimitat) must survive to the API as null.
        maxQuantity: values.maxQuantity ?? null,
        preorderQuantity: values.preorderQuantity,
        availability: values.availability,
        status: values.status,
        active: bool(values.active),
        activeOnline: bool(values.activeOnline),
        service: bool(values.service),
        prepay: bool(values.prepay),
        preorderAvailable: bool(values.preorderAvailable),
        availableFrom: values.availableFrom || null,
        preorderStart: values.preorderStart || null,
        preorderEnd: values.preorderEnd || null,
        // Renumber positions from array order: move/remove in the form only
        // reorder the array, and the gallery sorts by `position` server-side.
        // The spread carries `_pendingDesignJson` through when the DesignEditor
        // attached one, so saveImages can upsert the ImageDesign row.
        images: values.images.map((img, i) => ({ ...img, position: i })),
        tagIds: values.tagIds,
    };
    // Omit-means-keep: the API destroys + reinserts attributes/recipe only when
    // the key is present in the payload. A form that never loaded them
    // (undefined) must omit the keys or every save wipes both.
    if (values.attributes !== undefined) {
        payload.attributes = values.attributes
            .filter((a) => a.value !== '' && a.value !== null && a.value !== undefined)
            .map((a) => ({ id: a.id, value: String(a.value) }));
    }
    if (values.recipe !== undefined) {
        payload.recipe = values.recipe.map((r) => ({
            sourceProductId: r.sourceProductId ?? 0,
            sourceQuantity: r.sourceQuantity,
        }));
    }
    return payload;
}

// Product search for pickers: reuse `useSearchProductsQuery` from
// features/orders/orders.api (GET /products, q + 25 rows) — do not redeclare.
export const {
    useListProductsQuery,
    useGetProductQuery,
    useCreateProductMutation,
    useUpdateProductMutation,
    useDeleteProductMutation,
    useActivateProductMutation,
    useDeactivateProductMutation,
    useSetProductStatusMutation,
    useDuplicateProductMutation,
    useListCategoriesQuery,
    useListSuppliersQuery,
    useListUnitsQuery,
    useBulkSetActiveMutation,
    useBulkSetStatusMutation,
    useBulkAddTagsMutation,
    useBulkSetCategoryMutation,
    useBulkDeleteMutation,
    useImportProductsCsvMutation,
    useTransferStockMutation,
    useOutstandingTransferQuery,
    useGetCategoryDetailQuery,
    useListStockAccountsQuery,
    // Lazy (event-driven) — fired from the image-gallery "Editeaza" handler,
    // not subscribed declaratively.
    useLazyProxyImageQuery,
    useLazyGetImageDesignQuery,
} = productsApi;
